import { Grid, Paper, Typography } from '@material-ui/core';
import { postCustomerReverberations } from 'api/customersApi';
import {
  addOnlineReservationOldCustomer, addOnlineReservationsNewCustomer, sentMailReservation,
} from 'api/onlineBookingApi';
import verifyingTheUserResponseByTokenApi from 'api/recaptchaApi';
import PillButton from 'components/atoms/Buttons/PillButton';
import ScreenLoader from 'components/organismos/ScreenLoader';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { stringify } from 'qs';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { FinishBookingData, setReservationBooking } from 'redux/reservationBooking/actions';
import { Store } from 'redux/store';
import { setLocalStorage } from 'utility/browserStorageUtil';
import useStyles from '../styles';

declare global {
  interface Window { grecaptcha: any; }
}

function ConfirmBookingPage() {
  const location = useLocation();
  const dispatchRedux = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const urlSearchParam = new URLSearchParams(location.search);
  const courseCode = urlSearchParam.get('courseCode') || '';
  const companyCodeUrl = urlSearchParam.get('companyCode') || '';
  const history = useHistory();
  const timeOutRef = useRef<any>(null);
  const [isLoading, setLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const CAPTCHA_SITE_KEY = process.env.REACT_APP_CAPTCHA_SITE_KEY;
  const GOOGLE_RECAPTCHA_V3_SCRIPT = 'https://www.google.com/recaptcha/api.js';
  const SCRIPT_ID = 'google-recaptcha-v3';
  const GOOGLE_RECAPTCHA_BADGE_CLASSNAME = '.grecaptcha-badge';
  const LANGUAGE = 'ja';
  const { dataFormSubmit } = useSelector((reduxState: Store) => reduxState.reservationBookingOnline);
  const classes = useStyles();

  const handleError = useCallback((error: any) => {
    if (!error?.response) return;
    const errorResponse = error?.response?.data?.errors || error?.response?.data;
    if (errorResponse?.length && Array.isArray(errorResponse)) {
      errorResponse.forEach((err: any) => {
        enqueueSnackbar(err.defaultMessage ? err.defaultMessage : err.error, { variant: 'error' });
      });
    } else {
      enqueueSnackbar(error?.response?.data?.defaultMessage || error?.response?.error || error?.response?.data?.message || 'サーバー側でエラーが発生しました。', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // Begin:: ReCaptcha
  const loadScriptByURL = (id:string, url:string) => {
    const isScriptExist = document.getElementById(id);
    if (!isScriptExist) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = url;
      script.id = id;
      document.body.appendChild(script);
    }
  };

  const isCheckTokenCaptcha = useCallback(() => new Promise((resolve) => {
    window?.grecaptcha.ready(() => {
      window?.grecaptcha?.execute(CAPTCHA_SITE_KEY, { action: 'submit' }).then(async (token:string) => {
        const res = await verifyingTheUserResponseByTokenApi(token);
        const isCheckVerified = (res?.success && Number(res?.score ?? 0) >= 0.5);
        setIsVerified(isCheckVerified);
        resolve(isCheckVerified);
      });
    });
  }), [CAPTCHA_SITE_KEY]);

  const removeScript = useCallback((id:string) => {
    const isScriptExist = window.document.getElementById(id);
    if (isScriptExist) {
      isScriptExist.remove();
    }
  }, []);

  const removeGReCaptchaDivElement = useCallback(() => {
    if (!window) {
      return;
    }
    const element = window.document.querySelector(
      GOOGLE_RECAPTCHA_BADGE_CLASSNAME,
    );
    if (element && element.parentElement) {
      element.parentElement.remove();
    }
  }, []);

  // End:: ReCaptcha

  const postReverberationData = async (data: FinishBookingData, companyCode:string = '') => {
    const formatObjectForCustomerReverberation = {
      lastVisitDate: '',
      firstVisitDate: moment(data?.createDate).format('YYYY-MM-DD'),
      firstPersonStaffId: data?.responsibleStaffId ?? '',
      mediaId: data?.mediaId,
      courseCategoryId: data?.courseCategoryId,
      contractCourseMstId: data?.contractCourseId || '',
      reverberationDate: new Date(moment(data?.createDate).utcOffset(9).format('YYYY-MM-DD')).toISOString(),
      customerBasicDataId: data?.customerBasicDataId,
    };
    await postCustomerReverberations(formatObjectForCustomerReverberation, companyCode || '');
  };

  const onSubmitWithReCAPTCHA = useCallback(async () => {
    if (!dataFormSubmit) {
      return;
    }
    const isChecked:boolean = await isCheckTokenCaptcha();
    if (!isChecked) {
      enqueueSnackbar('You are Robot', { variant: 'error' });
      return;
    }
    try {
      setLoading(true);
      const dataSetting = {
        desiredCourse: dataFormSubmit?.desiredCourse,
        orgId: dataFormSubmit?.orgId,
        orgName: dataFormSubmit?.orgName,
        courseTime: dataFormSubmit?.courseTime,
        treatmentTime: dataFormSubmit?.treatmentTime,
        courseCategoryId: dataFormSubmit?.courseCategoryId,
        email: dataFormSubmit?.email || '',
      };
      let responseData;
      if (dataFormSubmit?.customerType === '0') { // for new customer
        responseData = await addOnlineReservationsNewCustomer(dataFormSubmit?.objectToPost?.newCustomer, dataFormSubmit?.mediaCode);
        postReverberationData({
          ...responseData,
          ...dataSetting,
        }, dataFormSubmit?.companyCode);
      } else { // for old customer
        responseData = await addOnlineReservationOldCustomer(dataFormSubmit?.objectToPost?.oldCustomer);
      }
      enqueueSnackbar('仮予約しました。', { variant: 'success' });
      dispatchRedux(setReservationBooking({
        ...responseData,
        ...dataSetting,
      }));
      sentMailReservation({ reservationId: responseData?.reservationId, companyCode: dataFormSubmit?.companyCode || '', customerType: dataFormSubmit?.customerType || '0' });
      setLocalStorage('companyCode', dataFormSubmit?.companyCode || '');
      setLocalStorage('mediaCode', dataFormSubmit?.mediaCode || '');
      history.replace(`/contact/finish?${stringify({ mediaCode: dataFormSubmit?.mediaCode, companyCode: dataFormSubmit?.companyCode || '' })}`);
    } catch (error:any) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [dataFormSubmit, dispatchRedux, enqueueSnackbar, handleError, history, isCheckTokenCaptcha]);


  const handleBack = useCallback(() => {
    const stringifyParam:any = { mediaCode: dataFormSubmit?.mediaCode, companyCode: dataFormSubmit?.companyCode || companyCodeUrl };
    if (courseCode) {
      stringifyParam.courseCode = courseCode;
    }
    if (dataFormSubmit?.customerType === '0') {
      // for new customer
      history.replace(`/contact/reserve?${stringify(stringifyParam)}`);
    } else {
      // for old customer
      history.replace(`/contact/reservem?${stringify(stringifyParam)}`);
    }
  }, [companyCodeUrl, courseCode, dataFormSubmit, history]);

  useEffect(() => {
    if (!dataFormSubmit?.objectToPost?.newCustomer && !dataFormSubmit?.objectToPost?.oldCustomer) {
      handleBack();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateSize = () => {
    if (timeOutRef.current) {
      clearTimeout(timeOutRef.current);
    }
    timeOutRef.current = setTimeout(() => {
      const height = document.getElementsByTagName('body')[0]?.clientHeight || 0;
      const obj = { event: 'iframe_height', height };
      // 親へiframeの高さを送信
      // postMessage(<送信する値>, <送信先のドメイン>)
      window.parent.postMessage(obj, 'https://www.ci-z.com');
    }, 500);
  };

  useEffect(() => {
    if (dataFormSubmit?.companyCode === 'ci_zlabo' && dataFormSubmit?.customerType === '0') {
      window.parent.postMessage({
        event: 'page_view',
        path: location?.pathname || '',
        campaign: '',
        page: 'application',
        application_number: '',
        mail: '',
      }, 'https://www.ci-z.com');
    }
    if (dataFormSubmit?.companyCode === 'ci_zlabo') {
      updateSize && updateSize();
      window.addEventListener('resize', updateSize);
    }
    return () => {
      window.removeEventListener('resize', updateSize);
      clearTimeout(timeOutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // load the script by passing the URL
    loadScriptByURL(SCRIPT_ID, `${GOOGLE_RECAPTCHA_V3_SCRIPT}?render=${CAPTCHA_SITE_KEY}&hl=${LANGUAGE}`);
    return () => {
      removeScript(SCRIPT_ID);
      removeGReCaptchaDivElement();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ScreenLoader isLoading={isLoading}>
      <div>
        <Typography className={classes.cssTitle}>
          以下の内容で仮予約を登録します。
          <br />
          よろしければ、仮予約するボタンをクリックしてください。
          <br />
          修正する場合は戻るボタンをクリックしてください。
        </Typography>
      </div>
      <div style={{
        maxWidth: '640px', width: '100%', marginTop: '1.5rem', marginBottom: '1.5rem',
      }}
      >
        <Paper className={classes.inputBoxV2}>
          <Typography variant="button" align="center" className={classes.labelFillBefore}>
            氏名
          </Typography>
          <Typography className={`${classes.inputTextLeft} ${classes.cssFontSize}`}>
            { dataFormSubmit?.customerType === '0' ? `${dataFormSubmit?.objectToPost?.newCustomer?.customerLastName || ''} ${dataFormSubmit?.objectToPost?.newCustomer?.customerFirstName || ''}` : `${dataFormSubmit?.objectToPost?.oldCustomer?.customerLastName || ''} ${dataFormSubmit?.objectToPost?.oldCustomer?.customerFirstName || ''}`}
          </Typography>
        </Paper>
        <Paper className={classes.inputBoxV2}>
          <Typography variant="button" align="center" className={classes.labelFillBefore}>
            メールアドレス
          </Typography>
          <Typography className={`${classes.inputTextLeft} ${classes.cssFontSize}`}>
            {dataFormSubmit?.email || ''}
          </Typography>
        </Paper>
        <Paper className={classes.inputBoxV2}>
          <Typography variant="button" align="center" className={classes.labelFillBefore}>
            予約日時
          </Typography>
          <Typography className={`${classes.inputTextLeft} ${classes.cssFontSize}`}>
            {dataFormSubmit?.courseTime && moment(dataFormSubmit?.courseTime ?? new Date()).format('YYYY年MM月DD日HH時mm分')}
            { dataFormSubmit?.courseTime && '〜' }
            {dataFormSubmit?.courseTime && moment(dataFormSubmit?.courseTime ?? new Date()).add(dataFormSubmit?.treatmentTime ?? 0, 'minutes').format('HH時mm分')}
          </Typography>
        </Paper>
        <Paper className={classes.inputBoxV2}>
          <Typography variant="button" align="center" className={classes.labelFillBefore}>
            店舗
          </Typography>
          <Typography className={`${classes.inputTextLeft} ${classes.cssFontSize}`}>
            {dataFormSubmit?.orgName || ''}
          </Typography>
        </Paper>
        <Paper className={classes.inputBoxV2}>
          <Typography variant="button" align="center" className={classes.labelFillBefore}>
            希望コース
          </Typography>
          <Typography className={`${classes.inputTextLeft} ${classes.cssFontSize}`}>
            {dataFormSubmit?.desiredCourse || ''}
          </Typography>
        </Paper>
      </div>
      <Grid container={true}>
        <Grid item={true} lg={10}>
          {dataFormSubmit && (
            <span style={{ marginRight: '2rem' }}>
              <PillButton
                type="button"
                color="primary"
                variant="contained"
                style={{ width: '200px' }}
                onClick={onSubmitWithReCAPTCHA}
                // disabled={!isVerified}
              >
                <strong>仮予約する</strong>
              </PillButton>
            </span>
          )}
          <span>
            <PillButton
              style={{ background: '#948A8A', color: 'white', width: '200px' }}
              color="inherit"
              type="button"
              variant="contained"
              onClick={handleBack}
            >
              <strong>戻る</strong>
            </PillButton>
          </span>
        </Grid>
      </Grid>
    </ScreenLoader>
  );
}

export default ConfirmBookingPage;
